/**
 * Returning a player to the external lobby (apps/lobby).
 *
 * Non-full-navigation tenants (DK) have no in-app Lobby tab — their lobby is a separately
 * deployed app that launches contests with `?from=lobby&lobbyReturn=…`. Those params are
 * captured into sessionStorage on first land so the way back survives later navigation
 * inside the pick'em app.
 *
 * Both exits — the "Back to Lobby" bar and the post-submission modal CTA — go through here
 * so they resolve the same destination and consume the same session state.
 */

import { resolveLobbyReturnUrl, safeLobbyReturnUrl } from '@low6-pickem/frontend-shared';

/** Set once the player is known to have arrived from the lobby. */
export const LOBBY_FROM_SESSION_KEY = 'pickem_from_lobby';
/** The exact lobby URL to return to, resolved on first land. */
export const LOBBY_RETURN_URL_KEY = 'pickem_lobby_return_url';

/**
 * Record that this session came from the lobby, and the URL to go back to.
 * Safe to call repeatedly; the freshest `lobbyReturn` param wins.
 */
export function rememberLobbyOrigin(searchParams: URLSearchParams): void {
  sessionStorage.setItem(LOBBY_FROM_SESSION_KEY, '1');
  sessionStorage.setItem(
    LOBBY_RETURN_URL_KEY,
    resolveLobbyReturnUrl(searchParams, LOBBY_RETURN_URL_KEY)
  );
}

/**
 * Whether the player reached this contest from the external lobby — either on this URL or
 * earlier in the session.
 *
 * Without such context there is nothing to return to: `resolveLobbyReturnUrl` would fall back
 * to a lobby path on the pick'em app's own origin, which does not serve one.
 */
export function hasLobbyOrigin(searchParams: URLSearchParams): boolean {
  return (
    searchParams.get('from') === 'lobby' || sessionStorage.getItem(LOBBY_FROM_SESSION_KEY) === '1'
  );
}

/**
 * Whether the post-entry popup should offer "Return to Lobby" at all.
 *
 * Two things both have to hold. The tenant has to be one that runs a lobby: only
 * non-full-navigation tenants (DK) do, and SkyPicks — which is full-navigation and has no
 * lobby anywhere in its product — is excluded here rather than relying on the session
 * check below to happen to come out false. And this particular session has to have come
 * from that lobby, since a DK player who deep-linked into a contest has nothing to go
 * back to either.
 *
 * When this is false the player stays in-app and lands on My Picks instead.
 */
export function hasLobbyDestination(
  pickemFullNavigation: boolean,
  searchParams: URLSearchParams
): boolean {
  if (pickemFullNavigation) return false;
  return hasLobbyOrigin(searchParams);
}

/**
 * Leave the pick'em app for the lobby. Returns false (and does nothing) when this session has
 * no lobby to go back to, so callers can fall back to in-app navigation.
 */
export function returnToLobby(searchParams: URLSearchParams): boolean {
  if (!hasLobbyOrigin(searchParams)) return false;

  const url =
    sessionStorage.getItem(LOBBY_RETURN_URL_KEY) ??
    resolveLobbyReturnUrl(searchParams, LOBBY_RETURN_URL_KEY);
  sessionStorage.removeItem(LOBBY_FROM_SESSION_KEY);
  sessionStorage.removeItem(LOBBY_RETURN_URL_KEY);
  window.location.href = safeLobbyReturnUrl(url);
  return true;
}
